/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import { Grid, Typography } from "@material-ui/core";
import PropTypes from "prop-types";
import CloseIcon from "@material-ui/icons/Close";
import CreateIcon from "@material-ui/icons/Create";
import { Card, CardBody, CardHeader, CardIcon } from "~/components/Card";
import PF2MDiscardChangesButton from "../PF2MDiscardChangesButton";
import PF2MFinalizeButton from "../PF2MFinalizeButton";
import styles from "./styles";
import { useTranslation } from "react-i18next";
import { jsonToImageMapper } from "./utils";
import {
  getDispatchCodeIcons,
  updateDispatchCodeIcons,
} from "~/store/dispatch/actions";
import { useDispatch, useSelector } from "react-redux";
import DeleteIcon from "@material-ui/icons/Delete";
import IconButton from "@material-ui/core/IconButton";

const IconCircle = ({ src, alt, size = 62, selected, onClick, label }) => (
  <div
    role="button"
    tabIndex={0}
    onClick={onClick}
    onKeyDown={(e) => {
      if (e.key === "Enter" || e.key === " ") onClick?.(e);
    }}
    style={{
      width: size,
      height: size,
      border: selected ? "2px solid #F2AA00" : "2px solid #ccc",
      ...styles.avatar,
    }}
  >
    {src ? (
      <img
        src={src}
        alt={alt || ""}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
        }}
      />
    ) : (
      <div style={styles.iconPlaceholder}>{label || ""}</div>
    )}
  </div>
);

IconCircle.propTypes = {
  src: PropTypes.string,
  alt: PropTypes.string,
  size: PropTypes.number,
  selected: PropTypes.bool,
  onClick: PropTypes.func,
  label: PropTypes.string,
};

const CodeIconModal = React.forwardRef(({ open, onClose, modalData }, _ref) => {
  const icons = require.context("../../assets/dispatch/icons", false, /\.svg$/);
  const iconPaths = icons.keys().map(icons);
  const { t: translate } = useTranslation();
  const dispatch = useDispatch();
  const [loadedData, setLoadedData] = useState(false);
  const dispatchCodeIcons = useSelector(
    (state) => state.dispatch.dispatchCodeIcons
  );
  const [selectedIcon, setSelectedIcon] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelectedIcon(null);
    }
    if (!loadedData) {
      dispatch(getDispatchCodeIcons());
      setLoadedData(true);
    }
  }, [open, loadedData, dispatch]);

  const filteredIcons = useMemo(() => {
    if (!dispatchCodeIcons || !modalData) return [];
    return dispatchCodeIcons
      .filter(
        (icon) =>
          icon.id_code === modalData.id && icon.id_equip === modalData.id_equip
      )
      .map((icon) => icon.icon_name);
  }, [dispatchCodeIcons, modalData]);

const currentIcon = useMemo(() => {
    // 1. Verifica se há ícones filtrados na store (Redux)
    const iconName = filteredIcons?.[0];
    if (iconName) {
      const iconType = iconName.split('.')[0];
      const matchedKey = icons
        .keys()
        .find((key) => key.includes(`${iconType}.svg`));
      if (matchedKey) return icons(matchedKey);
    }

    // 2. Fallback: jsonToImageMapper
    const match = jsonToImageMapper.find(
      (item) => item.id_code === modalData?.code_type
    );
    if (!match) return null;
    const iconKey = Object.keys(match).find((key) => key !== "id_code");
    return match[iconKey] || null;
  }, [filteredIcons, icons, modalData?.id]);

  if (!open) return null;

  const handleIconSelection = () => {
    const currentItem = dispatchCodeIcons.find(
      (icon) =>
        icon.id_code === modalData?.id && icon.id_equip === modalData?.id_equip
    );

    const currentIconName = currentItem?.icon_name;

    const iconName = selectedIcon
      ?.split("/")
      .pop()
      .replace(/\.svg$/, "")
      .replace(/\.[a-f0-9]{8}$/, "");

    const isUpdate = !!currentItem && currentIconName !== iconName;

    const payload = {
      id_code: modalData?.id,
      id_equip: modalData?.id_equip,
      icon_name: iconName.split('.')[0],
      update_status: isUpdate ? "U" : "I",
    };

    if (isUpdate && currentItem?.id) {
      payload.id = currentItem.id;
    }

    dispatch(updateDispatchCodeIcons([payload]));
    onClose();
  };

  const handleIconDelete = () => {
    const currentItem = dispatchCodeIcons.find(
      (icon) =>
        icon.id_code === modalData?.id && icon.id_equip === modalData?.id_equip
    );

    if (currentItem?.id) {
      const payload = {
        id: currentItem.id,
        id_code: modalData?.id,
        id_equip: modalData?.id_equip,
        icon_name: "",
        update_status: "D",
      };
      dispatch(updateDispatchCodeIcons([payload]));
    }
  };

  return (
    <>
      {/* Overlay escuro */}
      <div style={styles.overlay} onClick={onClose} />

      {/* Modal */}
      <div style={styles.modalContainer}>
        <Card style={styles.card}>
          <CardHeader icon>
            <>
              <CardIcon color="warning">
                <CreateIcon />
              </CardIcon>
              <CardIcon style={styles.headerContainer} onClick={onClose}>
                <CloseIcon />
              </CardIcon>
            </>
          </CardHeader>
          <CardBody style={styles.cardBody}>
            <div style={styles.contentContainer}>
              <Typography variant="h4" style={styles.title}>
                {translate("common:CodeIcon")}: {modalData?.id_group_name}
              </Typography>

              <Grid
                container
                spacing={4}
                justifyContent="center"
                alignItems="center"
              >
                <Grid item xs={6} style={styles.inlineContainer}>
                  <Typography variant="h6" style={styles.inlineText}>
                    {translate("common:Preview")}
                  </Typography>
                  <IconCircle
                    alt={translate("common:Preview")}
                    src={selectedIcon}
                    size={100}
                  />
                </Grid>

                <Grid item xs={6} style={styles.inlineContainer}>
                  <Typography variant="h6" style={styles.inlineText}>
                    {translate("common:CurrentlyInUse")}
                  </Typography>

                  <div
                    style={{
                      position: "relative",
                      width: 100,
                      height: 100,
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    <IconCircle
                      alt={translate("common:CurrentlyInUse")}
                      src={currentIcon}
                      size={100}
                    />

                    {currentIcon && isHovered && filteredIcons?.length > 0 && (
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundColor: "rgba(0,0,0,0.5)",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <IconButton
                          onClick={handleIconDelete}
                          style={{
                            background: "white",
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                          }}
                          size="small"
                        >
                          <DeleteIcon style={{ color: "red", fontSize: 20 }} />
                        </IconButton>
                      </div>
                    )}
                  </div>
                </Grid>
              </Grid>

              <div style={styles.iconContainer}>
                <Grid container spacing={2}>
                  {iconPaths.map((iconSrc, index) => (
                    <Grid item xs={2} key={index}>
                      <IconCircle
                        src={iconSrc}
                        alt={`Ícone ${index}`}
                        selected={selectedIcon === iconSrc}
                        onClick={() => setSelectedIcon(iconSrc)}
                        size={62}
                      />
                    </Grid>
                  ))}
                </Grid>
              </div>
            </div>
          </CardBody>
          <footer style={styles.footer}>
            <PF2MDiscardChangesButton onClick={() => setSelectedIcon(null)} />
            <PF2MFinalizeButton
              onClick={() => {
                handleIconSelection();
              }}
              disabled={!selectedIcon}
            />
          </footer>
        </Card>
      </div>
    </>
  );
});

CodeIconModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  modalData: PropTypes.object,
};

CodeIconModal.defaultProps = {
  modalData: {},
};

export default CodeIconModal;
